import React from 'react';
import { ReportWidget, ChartType } from '../../types/visualization';
import MetricCard from './MetricCard';
import BarChart from './BarChart';
import StackedBarChart from './StackedBarChart';
import DualAxisBarChart from './DualAxisBarChart';
import DualAxisLineChart from './DualAxisLineChart';
import LineChart from './LineChart';
import PieChart from './PieChart';
import BarChart3D from './BarChart3D';
import PieChart3D from './PieChart3D';

interface ChartRendererProps {
  widget: ReportWidget;
  data: any[];
}

const DEFAULT_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];

const ChartRenderer: React.FC<ChartRendererProps> = ({ widget, data }) => {
  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  // Use new protocol if available, otherwise fall back to legacy
  const chartType = widget.chartType || widget.visualization_type;
  const config = widget.config;
  const colors = config?.colors || DEFAULT_COLORS;

  // Single value metric display
  if (chartType === 'metric' || (data.length === 1 && Object.keys(data[0]).length === 1)) {
    const key = Object.keys(data[0])[0];
    const value = Object.values(data[0])[0];
    const title = config?.title || widget.visualization_config?.title || 
      key.replace(/(_|\b\w)/g, (m, p1) => p1 === '_' ? ' ' : p1.toUpperCase());
    const description = config?.description || widget.visualization_config?.description;

    return (
      <MetricCard 
        value={value}
        title={title}
        description={description}
        color={colors[0]}
      />
    );
  }

  // Table view
  if (chartType === 'table') {
    return (
      <div className="widget-table">
        <table className="table">
          <thead>
            <tr>
              {Object.keys(data[0]).map(key => (
                <th key={key}>{key.replace(/(_|\b\w)/g, (m, p1) => p1 === '_' ? ' ' : p1.toUpperCase())}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 10).map((row, idx) => (
              <tr key={idx}>
                {Object.values(row).map((value, vidx) => (
                  <td key={vidx}>{String(value)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {data.length > 10 && (
          <div style={{ padding: '8px', fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
            Showing first 10 of {data.length} results
          </div>
        )}
      </div>
    );
  }

  // Chart selection based on type
  switch (chartType as ChartType) {
    case 'bar':
      return <BarChart data={data} config={config} colors={colors} />;
    
    case 'stacked_bar':
      return <StackedBarChart data={data} config={config} colors={colors} />;
    
    case 'dual_axis_bar':
      return <DualAxisBarChart data={data} config={config} colors={colors} />;
    
    case 'line':
      return <LineChart data={data} config={config} colors={colors} />;
    
    case 'dual_axis_line':
      return <DualAxisLineChart data={data} config={config} colors={colors} />;
    
    case 'pie':
      return <PieChart data={data} config={config} colors={colors} />;
    
    case 'bar_3d':
      return <BarChart3D data={data} config={config} colors={colors} />;
    
    case 'pie_3d':
      return <PieChart3D data={data} config={config} colors={colors} />;
    
    // Unsupported chart types - fall back to table view
    default:
      return (
        <div>
          <div style={{ textAlign: 'center', padding: '12px 20px', color: '#f59e0b', fontSize: '13px', fontWeight: '500', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', marginBottom: '16px' }}>
            Chart type "{chartType}" coming soon - showing data as table
          </div>
          <div className="widget-table">
            <table className="table">
              <thead>
                <tr>
                  {Object.keys(data[0]).map(key => (
                    <th key={key}>{key.replace(/(_|\b\w)/g, (m, p1) => p1 === '_' ? ' ' : p1.toUpperCase())}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.slice(0, 10).map((row, idx) => (
                  <tr key={idx}>
                    {Object.values(row).map((value, vidx) => (
                      <td key={vidx}>{String(value)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {data.length > 10 && (
              <div style={{ padding: '8px', fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                Showing first 10 of {data.length} results
              </div>
            )}
          </div>
        </div>
      );
  }
};

export default ChartRenderer; 